import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Outlet, useSearchParams } from 'react-router-dom';
import { SignedIn, SignedOut, SignInButton, SignUpButton } from '@clerk/clerk-react';
import { useUserRole } from './hooks/useUserRole';
import { ToastProvider } from './components/Toast';
import Layout from './components/Layout';
import StudentLayout from './components/StudentLayout';
import Panel from './pages/Panel';
import ClassNew from './pages/ClassNew';
import ClassDetail from './pages/ClassDetail';
import ClassAssignments from './pages/ClassAssignments';
import StudentDetail from './pages/StudentDetail';
import StudentJoin from './pages/StudentJoin';
import StudentAssignments from './pages/StudentAssignments';
import StudentClassDetail from './pages/StudentClassDetail';
import AssignmentDetail from './pages/AssignmentDetail';
import NotFound from './pages/NotFound';

const PENDING_CODE_KEY = 'bitmiyo_pending_class_code';

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-accent-500 to-accent-600 animate-pulse" />
        <p className="text-text-muted text-sm font-semibold">Yükleniyor...</p>
      </div>
    </div>
  );
}

function SignedOutScreen() {
  const [searchParams] = useSearchParams();
  const code = (searchParams.get('kod') ?? '').trim().toUpperCase();
  const redirectUrl = code
    ? `${window.location.origin}/katil?kod=${encodeURIComponent(code)}`
    : `${window.location.origin}${window.location.pathname}`;

  useEffect(() => {
    if (code) localStorage.setItem(PENDING_CODE_KEY, code);
  }, [code]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
      <img src="/logo_miyo.webp" alt="Bitmiyo" className="w-16 h-16 object-contain mb-6" />
      <h1 className="text-3xl md:text-4xl font-black mb-3 text-text-primary" style={{ fontFamily: 'var(--font-heading)' }}>
        Bitmiyo <span className="text-accent-600 dark:text-accent-500">Sınıf</span>
      </h1>
      <p className="text-text-secondary text-sm md:text-base max-w-md mb-8 leading-relaxed font-medium">
        Öğretmenler ve öğrenciler için canlı ders takibi, ödev yönetimi ve koçluk platformu.
      </p>

      {code && (
        <div className="mb-6 p-4 rounded-2xl bg-accent-100 dark:bg-accent-500/10 border border-accent-200 dark:border-accent-500/20 max-w-sm w-full">
          <p className="text-xs font-bold text-accent-600 dark:text-accent-400 mb-1">
            Sınıf Katılım Kodu Algılandı
          </p>
          <p className="text-lg font-black font-mono tracking-widest text-text-primary">
            {code}
          </p>
          <p className="text-[11px] text-text-muted mt-1">
            Giriş yaptıktan sonra sınıfa otomatik yönlendirileceksiniz.
          </p>
        </div>
      )}

      <div className="flex gap-3">
        <SignInButton mode="modal" forceRedirectUrl={redirectUrl}>
          <button className="bitmiyo-btn bitmiyo-btn-outline">Giriş Yap</button>
        </SignInButton>
        <SignUpButton mode="modal" forceRedirectUrl={redirectUrl}>
          <button className="bitmiyo-btn bitmiyo-btn-primary">Hesap Oluştur</button>
        </SignUpButton>
      </div>
    </div>
  );
}

// Layout/StudentLayout `children` aldığı için, layout'lu sayfaları bir layout-route
// altında toplayabilmek adına Outlet'e köprü kuran ince kabuklar. 404 bu kabukların
// DIŞINDA kalır — böylece navbar/sidebar 404 sayfasında görünmez.
function TeacherShell() {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function StudentShell() {
  return (
    <StudentLayout>
      <Outlet />
    </StudentLayout>
  );
}

function TeacherApp() {
  return (
    <Routes>
      <Route element={<TeacherShell />}>
        <Route path="/" element={<Panel />} />
        <Route path="/panel" element={<Panel />} />
        <Route path="/sinif/yeni" element={<ClassNew />} />
        <Route path="/sinif/:classId" element={<ClassDetail />} />
        <Route path="/sinif/:classId/gorevler" element={<ClassAssignments />} />
        <Route path="/sinif/:classId/odev/:assignmentId" element={<AssignmentDetail />} />
        <Route path="/sinif/:classId/ogrenci/:studentId" element={<StudentDetail />} />
      </Route>
      <Route
        path="*"
        element={
          <NotFound
            homePath="/panel"
            homeLabel="Panele Dön"
            menuLinks={[
              { to: '/panel', label: 'Panel' },
              { to: '/sinif/yeni', label: 'Yeni Sınıf' },
            ]}
          />
        }
      />
    </Routes>
  );
}

function StudentApp() {
  return (
    <Routes>
      <Route element={<StudentShell />}>
        <Route path="/" element={<StudentAssignments />} />
        <Route path="/gorevlerim" element={<StudentAssignments />} />
        <Route path="/katil" element={<StudentJoin />} />
        <Route path="/katil/:codeParam" element={<StudentJoin />} />
        <Route path="/join/:codeParam" element={<StudentJoin />} />
        <Route path="/sinif/:classId" element={<StudentClassDetail />} />
        <Route path="/sinif/:classId/odev/:assignmentId" element={<AssignmentDetail />} />
        <Route path="/odev/:assignmentId" element={<AssignmentDetail />} />
      </Route>
      <Route
        path="*"
        element={
          <NotFound
            homePath="/gorevlerim"
            homeLabel="Görevlerime Dön"
            menuLinks={[
              { to: '/gorevlerim', label: 'Görevlerim' },
              { to: '/katil', label: 'Sınıfa Katıl' },
            ]}
          />
        }
      />
    </Routes>
  );
}

function Gate() {
  const { isLoaded, isTeacher } = useUserRole();
  if (!isLoaded) return <LoadingScreen />;
  return isTeacher ? <TeacherApp /> : <StudentApp />;
}

export default function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        {/* Ambient dekoratif arka plan */}
        <div className="fixed inset-0 pointer-events-none -z-10 bg-canvas overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808010_1px,transparent_1px),linear-gradient(to_bottom,#80808010_1px,transparent_1px)] bg-[size:24px_24px]" />
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-accent-500/10 blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-blue-300/10 dark:bg-blue-900/10 blur-[120px]" />
          {/* Şahin filigranı — bitmiyo ana sitedeki "Ambient Arka Plan" deseniyle aynı */}
          <img
            src="/baslat.webp"
            alt=""
            aria-hidden="true"
            style={{ transform: 'rotate(18deg)' }}
            className="absolute -bottom-24 right-4 w-[900px] h-[900px] object-contain opacity-[0.09] dark:opacity-[0.14] dark:invert transition-opacity duration-500 select-none pointer-events-none"
          />
        </div>

        <SignedOut>
          <SignedOutScreen />
        </SignedOut>
        <SignedIn>
          <Gate />
        </SignedIn>
      </BrowserRouter>
    </ToastProvider>
  );
}
